import { useEffect, useMemo, useState } from "react";
import { allColumns, applyPins, cellText, frameIndexMap } from "../lib/columns";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { useColumnPins, type PinSide } from "../lib/columnPins";
import { ledgerColumns, sortPositions, type SortSpec } from "../lib/ledgerView";
import { matchGroupCellClass } from "../lib/matchGroups";
import { useFrozenOffsets } from "../lib/frozenOffsets";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";
import EmptyState from "./EmptyState";
import FullscreenTable from "./FullscreenTable";
import { HeadControls } from "./GridControls";
import { SearchPanel, useMultiSearch } from "./SearchPanel";

interface Props {
  frame: FrameRecords;
  config?: SideDisplayConfig;
  indices: (number | string | null)[]; // bucket rows, by original frame index
  pinSide?: PinSide;
  insurerName?: string;
  // Title shown in the full-screen header bar.
  title?: string;
}

const PAGE = 25;

// The inline transaction table under a dashboard subtype line — mirrors the old
// Streamlit nested expander (app/tables.py::_render_transaction_table): curated
// columns first then every other populated column, dd/mm/yyyy dates, em-dash blanks,
// original row order until the user sorts. Rows show LITERAL frame values (raw/export
// sign convention); only the bucket header amount carries display signing — never "fix"
// signs here. The matching fields lead and repeated columns go last (ledgerColumns).
// Every column carries the same in-header filter as the statement grids, but scoped
// to THIS bucket's rows (distinct values + predicate over the bucket, not the frame).
export default function SubtypeTable({ frame, config, indices, pinSide, insurerName = "", title }: Props) {
  const [page, setPage] = useState(0);
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({});
  const [sort, setSort] = useState<SortSpec | null>(null);
  const pins = useColumnPins(pinSide ?? "cri", insurerName);
  const pinnable = pinSide !== undefined;

  const positions = useMemo(() => {
    const m = frameIndexMap(frame);
    const out: number[] = [];
    for (const idx of indices) {
      const p = m.get(String(idx));
      if (p !== undefined) out.push(p);
    }
    return out;
  }, [frame, indices]);

  // The bucket as a frame of its own, so the search works over exactly these rows. Same
  // columns as `frame`, so every column index carries across.
  const bucket = useMemo<FrameRecords>(() => ({
    columns: frame.columns,
    rows: positions.map((p) => frame.rows[p]),
    indices: positions.map((p) => frame.indices[p]),
    total_rows: positions.length,
  }), [frame, positions]);

  const cols = useMemo(() => {
    const resolved = ledgerColumns(allColumns(frame, config, positions), bucket, bucket.rows);
    return pinnable ? applyPins(resolved, pins.pinned) : resolved;
  }, [frame, config, positions, bucket, pinnable, pins.pinned]);

  const search = useMultiSearch(bucket, cols);
  const { text, setText, setSearchCol, setExact } = search;

  const frozen = useFrozenOffsets(pinnable ? pins.pinned : [], cols);
  const lastFrozen = useMemo(() => {
    let last: string | undefined;
    for (const c of cols) if (frozen.offsetFor(c.key) !== undefined) last = c.key;
    return last;
  }, [cols, frozen]);

  useEffect(() => {
    setColFilters({}); setOpenCol(null); setPage(0); setSort(null);
    setText(""); setSearchCol(""); setExact(false);
  }, [frame, indices, setText, setSearchCol, setExact]);

  const distinctFor = (colIndex: number): string[] => {
    const set = new Set<string>();
    for (const r of bucket.rows) {
      const v = r[colIndex];
      if (v !== null && v !== undefined && String(v).trim() !== "") set.add(String(v));
    }
    return [...set].sort();
  };

  const nActive = Object.values(colFilters).filter((v) => v.length > 0).length;
  // FRAME positions of the rows shown, in display order: the selection's row ids.
  const filtered = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v.length > 0)
      .map(([col, chosen]) => ({ i: frame.columns.indexOf(col), set: new Set(chosen) }))
      .filter((x) => x.i >= 0);
    const test = search.rowTest;
    const kept: number[] = [];
    for (let k = 0; k < bucket.rows.length; k++) {
      const r = bucket.rows[k];
      if (!active.every((a) => a.set.has(String(r[a.i] ?? "")))) continue;
      if (test && !test(k, r)) continue;
      kept.push(k);
    }
    const order = sortPositions(frame, kept.map((k) => bucket.rows[k]), sort);
    return (order ? order.map((i) => kept[i]) : kept).map((k) => positions[k]);
  }, [positions, bucket, colFilters, frame, search.rowTest, sort]);

  useEffect(() => { setPage(0); }, [colFilters, text, search.searchCol, search.exact, sort]);

  const nPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pagePositions = filtered.slice(page * PAGE, (page + 1) * PAGE);

  // Excel-style cell selection, the same hook the statement grids use. Row coordinates
  // are positions in `filtered` (not the page slice), so clicking a header foots the
  // whole bucket rather than the 25 rows on screen — which is the actual question here:
  // "what does this subtype come to?".
  const selRows = useMemo(() => filtered.map((p) => frame.rows[p]), [filtered, frame]);
  const colIdx = useMemo(() => cols.map((c) => c.index), [cols]);
  const colLabels = useMemo(() => cols.map((c) => c.label), [cols]);
  // Frame positions are already unique per row, so they ARE the stale-guard's row ids.
  const sel = useGridSelection(selRows, colIdx, colLabels, filtered);
  const cw = useColumnWidths(useMemo(() => cols.map((c) => c.key), [cols]), bucket);

  if (positions.length === 0) {
    return <EmptyState title="No rows in this subtype" />;
  }

  const narrowing = nActive > 0 || text.trim() !== "";

  return (
    <FullscreenTable label={title || "Transactions"}>
      {(scrollMax) => (
    <div className="subtype-toolbar">
      <SearchPanel search={search} matchingRows={filtered.length}
        tools={<>
          {nActive > 0 && (
            <button className="btn btn-xs subtype-clear" onClick={() => setColFilters({})}>
              Clear {nActive} filter{nActive === 1 ? "" : "s"}
            </button>
          )}
          {narrowing && (
            <span className="muted">
              {filtered.length.toLocaleString()} of {positions.length.toLocaleString()} rows
            </span>
          )}
        </>} />
      <div {...sel.gridProps}>
      <div ref={(el) => { frozen.scrollRef.current = el; sel.scrollHostRef.current = el; }}
        className="grid-scroll" style={{ maxHeight: scrollMax ?? 360 }}>
        <table className={"data-table" + (pinnable && pins.pinned.length ? " data-table--separate" : "") + cw.className}
          style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr>
              {cols.map((c, ci) => {
                const left = frozen.offsetFor(c.key);
                const cls = [
                  left !== undefined ? "pinned-col" + (c.key === lastFrozen ? " pinned-edge" : "") : "",
                  sel.isColSelected(ci) ? "col-selected" : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={c.key} data-col-key={c.key} className={`nowrap ${cls}`}
                    style={{ left }}>
                    <span className="th-inner">
                      {/* Only the LABEL selects the column — the filter and pin controls
                          sit outside it, so opening a filter never foots a column. */}
                      <span className="th-label" title="Click to total this column · Ctrl+click to add another"
                        onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                        {c.label}
                      </span>
                      <HeadControls frame={frame} col={c}
                        filter={{
                          active: (colFilters[c.key]?.length ?? 0) > 0,
                          open: openCol === c.key,
                          options: () => distinctFor(c.index),
                          selected: colFilters[c.key] ?? [],
                          onToggleOpen: () => setOpenCol(openCol === c.key ? null : c.key),
                          onChange: (v) => setColFilters((f) => ({ ...f, [c.key]: v })),
                        }}
                        sort={{ spec: sort, onSort: setSort }}
                        pin={pinnable ? { pinned: pins.isPinned(c.key), onToggle: () => pins.toggle(c.key) } : undefined} />
                    </span>
                    {/* Autofit reads every filtered row, not just this page. */}
                    {cw.grip(c.key, () => filtered.map((p) => cellText(frame.rows[p][c.index], c.key)))}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagePositions.map((p, ri) => {
              const rowPos = page * PAGE + ri;  // position in `filtered`, the selection's coordinate space
              return (
              <tr key={p}>
                {cols.map((c, ci) => {
                  const raw = frame.rows[p][c.index];
                  const text = cellText(raw, c.key);
                  const left = frozen.offsetFor(c.key);
                  const cls = [
                    typeof raw === "number" ? "num" : "",
                    left !== undefined ? "pinned-col" : "",
                    left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                    sel.isSelected(rowPos, ci) ? "cell-selected" : "",
                    matchGroupCellClass(c.key, raw),
                  ].filter(Boolean).join(" ");
                  return (
                    <td key={c.key} className={`cell-clip ${cls}`} title={text}
                      style={{ left }}
                      data-row={rowPos} data-col={ci}
                      onMouseDown={(e) => { sel.onCellMouseDown(rowPos, ci, e); sel.focusGrid(); }}
                      onMouseEnter={() => sel.onCellMouseEnter(rowPos, ci)}>
                      {text}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <EmptyState
          title="No rows match the current filters"
          action={{
            label: text.trim() ? "Clear search" : `Clear ${nActive} filter${nActive === 1 ? "" : "s"}`,
            onClick: () => { if (text.trim()) setText(""); else setColFilters({}); },
          }}
        />
      )}
      {sel.statusBar}
      </div>
      {nPages > 1 && (
        <div className="grid-pager">
          <button className="btn btn-pager" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
          <span className="muted">Page {page + 1} of {nPages} · {filtered.length.toLocaleString()} rows</span>
          <button className="btn btn-pager" onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))} disabled={page >= nPages - 1}>Next</button>
        </div>
      )}
    </div>
      )}
    </FullscreenTable>
  );
}
